<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ContactReasonModal from './ContactReasonModal.vue';
import SopViewerModal from './SopViewerModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import LinkedConversation from './LinkedConversation.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showContactReasonModal, toggleContactReasonModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showSopModal, toggleSopModal] = useToggle(false);
const [showSopDropdown, toggleSopDropdown] = useToggle(false);
const activeSop = ref({ id: '', title: '' });
const showSopHint = ref(false);
let sopHintTimer = null;

const currentChat = computed(() => store.getters.getSelectedChat);

const contactReasonsCount = computed(() => {
  return currentChat.value?.contact_reason_ids?.length || 0;
});

const currentInbox = computed(() =>
  store.getters['inboxes/getInboxById'](currentChat.value?.inbox_id)
);
const currentMarket = computed(() => currentInbox.value?.market);

const allContactReasons = computed(() =>
  useCamelCase(store.getters['contactReasons/getContactReasons'], {
    deep: true,
  })
);

const sopReasons = computed(() => {
  const selectedIds = new Set(currentChat.value?.contact_reason_ids || []);
  const market = currentMarket.value;

  const findWithSop = reasons => {
    const result = [];
    for (const reason of reasons) {
      if (selectedIds.has(reason.id)) {
        const entry = reason.config?.sopUrls?.find(e => e.market === market);
        if (entry?.url) {
          result.push({ id: entry.url, title: reason.name });
        }
      }
      if (reason.childReasons?.length) {
        result.push(...findWithSop(reason.childReasons));
      }
    }
    return result;
  };

  return findWithSop(allContactReasons.value);
});

const sopDropdownItems = computed(() =>
  sopReasons.value.map(sop => ({
    icon: 'i-lucide-workflow',
    label: sop.title,
    action: sop.id,
    value: sop.id,
  }))
);

// Preload iframe + show hint callout whenever sopReasons becomes non-empty.
watch(
  sopReasons,
  (reasons, prev) => {
    if (reasons.length > 0 && !activeSop.value.id) {
      activeSop.value = reasons[0];
    }
    // Show hint only when SOP reasons appear for the first time (transition 0 → n).
    if (reasons.length > 0 && (!prev || prev.length === 0)) {
      clearTimeout(sopHintTimer);
      showSopHint.value = true;
      sopHintTimer = setTimeout(() => {
        showSopHint.value = false;
      }, 6000);
    }
    if (reasons.length === 0) {
      clearTimeout(sopHintTimer);
      showSopHint.value = false;
    }
  },
  { immediate: true }
);

const openSopViewer = sop => {
  activeSop.value = sop;
  toggleSopDropdown(false);
  toggleSopModal(true);
  clearTimeout(sopHintTimer);
  showSopHint.value = false;
};

const handleSopButtonClick = () => {
  if (sopReasons.value.length === 1) {
    openSopViewer(sopReasons.value[0]);
  } else {
    toggleSopDropdown();
  }
};

const handleSopDropdownAction = ({ action }) => {
  const sop = sopReasons.value.find(s => s.id === action);
  if (sop) openSopViewer(sop);
};

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
  clearTimeout(sopHintTimer);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <LinkedConversation :current-chat="currentChat" />
    <div
      v-if="sopReasons.length > 0"
      v-on-clickaway="() => toggleSopDropdown(false)"
      class="relative flex items-center"
    >
      <ButtonV4
        v-tooltip="$t('SOP_VIEWER.BUTTON_LABEL')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-workflow"
        class="rounded-md hover:bg-n-alpha-2"
        @click="handleSopButtonClick"
      />
      <Transition name="modal-fade">
        <div
          v-if="showSopHint"
          class="absolute top-full ltr:right-0 rtl:left-0 mt-2 z-50 w-56 bg-woot-600 text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none"
        >
          <div
            class="absolute -top-1 ltr:right-3 rtl:left-3 size-2 bg-woot-600 rotate-45"
          />
          {{ $t('SOP_VIEWER.HINT') }}
        </div>
      </Transition>
      <DropdownMenu
        v-if="showSopDropdown"
        :menu-items="sopDropdownItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleSopDropdownAction"
      />
    </div>
    <div class="relative">
      <ButtonV4
        v-tooltip="$t('CONTACT_REASON_MODAL.BUTTON_LABEL')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-tag"
        class="rounded-md hover:bg-n-alpha-2"
        @click="toggleContactReasonModal()"
      />
      <span
        v-if="contactReasonsCount === 0"
        class="absolute -top-0.5 -right-0.5 flex size-2.5"
      >
        <span
          class="absolute inline-flex size-full rounded-full bg-woot-500 opacity-75 animate-ping"
        />
        <span class="relative inline-flex rounded-full size-2.5 bg-woot-500" />
      </span>
      <span
        v-else
        class="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-4 h-4 px-0.5 text-[10px] font-semibold leading-none text-white bg-woot-500 rounded-full"
      >
        {{ contactReasonsCount }}
      </span>
    </div>
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <ContactReasonModal
      v-if="showContactReasonModal"
      :show="showContactReasonModal"
      :current-chat="currentChat"
      @cancel="toggleContactReasonModal"
    />
    <SopViewerModal
      :show="showSopModal"
      :sop-url="activeSop.id"
      :title="activeSop.title"
      @close="toggleSopModal(false)"
    />
  </div>
</template>
